import { AlertTriangle, Ban, Camera, Check, ClipboardCheck, Wrench as WrenchIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Read-only render of a job's execution block. Shared between:
 *   - Office JobDetail "Submitted Report" tab (embedded)
 *   - /jobs/:id/report + /my/jobs/:id/report routes (standalone)
 *   - PDF template (server-rendered; visually consistent)
 *
 * Props:
 *   job: hydrated job with execution + machines + customer + site + engineer.
 */
export default function SubmittedReport({ job }) {
  if (!job) return null;
  const exe = job.execution;
  if (!exe) {
    return (
      <Card className="rounded-md" data-testid="submitted-report-empty">
        <CardContent className="p-6 text-sm text-muted-foreground">No execution data has been submitted for this job yet.</CardContent>
      </Card>
    );
  }
  const machines = job.machines || [];
  const machineById = Object.fromEntries(machines.map((m) => [m.id, m]));
  const reportsByMachine = Object.fromEntries((exe.machines_report || []).map((r) => [r.machine_id, r]));
  const timeEntries = exe.time_entries || [];
  const photos = exe.photos || [];
  const sigs = exe.signatures || {};
  const totalSec = timeEntries.reduce((acc, e) => {
    if (!e.arrived_at || !e.left_at) return acc;
    const s = new Date(e.arrived_at).getTime();
    const l = new Date(e.left_at).getTime();
    return acc + Math.max(0, Math.floor((l - s) / 1000));
  }, 0);
  const totalTime = fmtDur(totalSec);

  return (
    <div className="space-y-4" data-testid="submitted-report">
      <Card className="rounded-md">
        <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Time on site</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div className="text-2xl font-bold font-mono" data-testid="submitted-total-time">{totalTime}</div>
          {timeEntries.length === 0 ? (
            <div className="text-muted-foreground italic">No sessions recorded.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left uppercase tracking-wider text-muted-foreground">
                  <th className="py-1.5 px-2">Arrived</th>
                  <th className="py-1.5 px-2">Left</th>
                  <th className="py-1.5 px-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {timeEntries.map((e, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`submitted-time-entry-${i}`}>
                    <td className="py-1.5 px-2">{fmtTs(e.arrived_at)}</td>
                    <td className="py-1.5 px-2">{fmtTs(e.left_at) || "—"}</td>
                    <td className="py-1.5 px-2 text-muted-foreground">{e.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {machines.map((m) => {
        const rep = reportsByMachine[m.id];
        const mt = m.machine_type || {};
        const responses = rep?.checklist_responses || [];
        const parts = rep?.parts_used || [];
        return (
          <Card key={m.id} className="rounded-md" data-testid={`submitted-machine-${m.id}`}>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <WrenchIcon className="h-4 w-4 text-primary" />
                {m.make} {m.model}
                <span className="text-xs font-normal text-muted-foreground">· S/N {m.serial} · {mt.name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Checklist</div>
                {responses.length === 0 ? <div className="text-xs text-muted-foreground italic">No responses.</div> : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left uppercase tracking-wider text-muted-foreground">
                        <th className="py-1.5 px-2">Item</th>
                        <th className="py-1.5 px-2 w-24">Answer</th>
                        <th className="py-1.5 px-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {responses.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="py-1.5 px-2">{r.label_snapshot || r.item_id}</td>
                          <td className="py-1.5 px-2 font-medium">{fmtAnswer(r.kind, r.value)}</td>
                          <td className="py-1.5 px-2 text-muted-foreground">{r.notes || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Parts used</div>
                {parts.length === 0 ? <div className="text-xs text-muted-foreground italic">None.</div> : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left uppercase tracking-wider text-muted-foreground">
                        <th className="py-1.5 px-2">SKU</th>
                        <th className="py-1.5 px-2">Name</th>
                        <th className="py-1.5 px-2 w-16">Qty</th>
                        <th className="py-1.5 px-2 w-20">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parts.map((p, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="py-1.5 px-2 font-mono">{p.sku || "—"}</td>
                          <td className="py-1.5 px-2">{p.name}</td>
                          <td className="py-1.5 px-2">{p.qty}</td>
                          <td className="py-1.5 px-2">{p.unit || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {rep?.recommendations && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Recommendations</div>
                  <div className="border-l-2 border-primary bg-primary/5 px-3 py-2 whitespace-pre-wrap">{rep.recommendations}</div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {job.actions_required && (
        <Card className="rounded-md border-amber-500/40 bg-amber-500/5" data-testid="submitted-actions-required">
          <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700 dark:text-amber-400"><AlertTriangle className="h-4 w-4" /> Actions required</CardTitle></CardHeader>
          <CardContent className="text-sm whitespace-pre-wrap">{exe.actions_required_notes || "—"}</CardContent>
        </Card>
      )}

      <Card className="rounded-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Camera className="h-4 w-4 text-primary" /> Photos</CardTitle>
          <span className="text-xs text-muted-foreground">{photos.length}</span>
        </CardHeader>
        <CardContent>
          {photos.length === 0 ? <div className="text-xs text-muted-foreground italic">No photos captured.</div> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="submitted-photo-grid">
              {photos.map((p) => (
                <a
                  key={p.file_id}
                  href={`/api/files/${p.file_id}`}
                  target="_blank" rel="noopener noreferrer"
                  className="block border border-border rounded-sm overflow-hidden bg-muted"
                  data-testid={`submitted-photo-${p.file_id}`}
                >
                  <img
                    src={`/api/files/${p.file_id}`}
                    alt={p.caption || "photo"}
                    className="w-full aspect-square object-cover"
                    onError={(e) => { e.target.style.opacity = 0.2; }}
                  />
                  {p.caption && <div className="text-[10px] px-1.5 py-0.5 truncate text-muted-foreground">{p.caption}</div>}
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-md" data-testid="submitted-signatures">
        <CardHeader><CardTitle className="text-sm font-medium">Signatures</CardTitle></CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-2">
          <SigBlock label="Engineer" sig={sigs.engineer} testId="submitted-sig-engineer" />
          <SigBlock label="Customer" sig={sigs.customer} testId="submitted-sig-customer" />
        </CardContent>
      </Card>
    </div>
  );
}

function SigBlock({ label, sig, testId }) {
  return (
    <div className="border border-border rounded-sm p-3 space-y-1.5" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      {!sig ? (
        <div className="text-sm text-muted-foreground italic">Not signed</div>
      ) : sig.refused ? (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 space-y-1" data-testid={`${testId}-refused`}>
          <div className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center gap-1"><Ban className="h-3.5 w-3.5" /> Refused</div>
          <div className="text-sm">{sig.reason}</div>
          {sig.recorded_at && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recorded {new Date(sig.recorded_at).toLocaleString()}</div>}
        </div>
      ) : (
        <>
          {sig.file_id && (
            <img
              src={`/api/files/${sig.file_id}`}
              alt={`${label} signature`}
              className="max-h-24 bg-white rounded-sm border border-border"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          <div className="text-sm flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-600" /><span className="font-medium">{sig.name || "—"}</span></div>
          {sig.signed_at && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Signed {new Date(sig.signed_at).toLocaleString()}</div>}
        </>
      )}
    </div>
  );
}

function fmtDur(sec) {
  if (!sec || sec < 0) return "0m";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}
function fmtTs(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString(); } catch { return v; }
}
function fmtAnswer(kind, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (kind === "yes_no") return value ? "Yes" : "No";
  if (kind === "ok_issue") return value === "ok" ? "OK" : "Issue";
  return String(value);
}
